import { Link } from "@readium/shared";
import { captureException } from "@sentry/browser";

import { AuthType } from "./session";
import { MODE } from "@/consts";
import { identity } from "@vueuse/core";
import { unsafeDecodeJwtPayload } from "../../modules/shared-utils/jwt";

export interface JwtOptions {
  // https://github.com/demarque/standards/blob/master/web-reader/access.md#customizing-the-reader
  language?: string; // Contains the preferred language for the viewer's interface. Takes precendence over the user agent's language
  definitions?: boolean; // Enable definitions lookup in the viewer
  noui?: boolean; // Disable the viewer's user interface
  interactiveMode?: boolean; // Trigger a specific display mode for interactive publications.
  showcaseTTS?: boolean; // Display TTS alongside the other primary actions.
  allowOffline?: boolean;
  links?: Link[];
}

const asIs: <T>(val: unknown) => T = identity as any;

export class Token {
  private values: Map<string, unknown> = new Map();
  private readonly links: Link[];
  constructor(
    public readonly type: AuthType,
    public readonly raw: string,
    private readonly jwt: JwtOptions | null
  ) {
    this.links = jwt?.links || [];
  }

  public get isSample(): boolean {
    return this.computeBool(
      "sample",
      () => this.type === "hub_sample" || this.type === "warehouse_sample"
    );
  }

  public get definitionsEnabled(): boolean {
    return this.computeOption("definitions", parseBool) ?? false;
  }

  public get uiEnabled(): boolean {
    return !this.computeOption("noui", parseBool, true);
  }

  public get language(): string | undefined {
    return this.computeOption("language", asIs);
  }

  public get interactiveMode(): boolean {
    return this.computeOption("interactiveMode", parseBool) ?? false;
  }

  public get showcaseTTS(): boolean {
    return this.computeOption("showcaseTTS", parseBool) ?? false;
  }

  public get allowOffline(): boolean {
    return this.computeOption("allowOffline", parseBool) ?? false;
  }

  public get backAffordance(): Link | undefined {
    return this.links.find((l) => l.rels?.has("prev"));
  }

  public get progressionEndpoint(): Link | undefined {
    return this.links.find(
      (l) => l.type === "application/vnd.readium.progression+json"
    );
  }

  private computeOption<K extends keyof JwtOptions>(
    key: K,
    parser: (val: unknown) => JwtOptions[K] | undefined,
    allowUrlOverride: boolean = false
  ): JwtOptions[K] {
    const compute = () => this.jwt?.[key];
    return this.computeValue<JwtOptions[K]>(
      key,
      parser,
      compute,
      allowUrlOverride
    );
  }

  private computeBool(
    key: string,
    compute: () => boolean,
    allowUrlOverride: boolean = false
  ): boolean {
    return this.computeValue<boolean>(
      key,
      parseBool,
      compute,
      allowUrlOverride
    );
  }

  private computeValue<T>(
    key: string,
    parser: (val: string) => T,
    compute: () => T,
    allowUrlOverride: boolean = false
  ): T {
    if (this.values.has(key)) {
      return this.values.get(key) as T;
    }

    if (allowUrlOverride || MODE !== "production") {
      const params = new URL(window.location.href).searchParams;
      if (params?.has(key)) {
        const value = parser(params.get(key));
        this.values.set(key, value);
        return value;
      }
    }

    const value = compute();
    this.values.set(key, value);
    return value;
  }
}

function parseBool(value: unknown): boolean {
  if (typeof value === "string") {
    // passed as argument to the url or to the token without value
    return value === "" || value.toLowerCase() === "true";
  }
  return false;
}

// raw parsing of JWT for quick access to non critical claims
export function parseToken(type: AuthType, token: string): Token {
  try {
    const decodedToken = unsafeDecodeJwtPayload(token);
    if (!decodedToken) {
      // Not a JWT, return a default token
      return new Token(type, token, null);
    }

    // @ts-expect-error access unsafe private field
    const data = decodedToken.unsafe_payload as JwtOptions;
    return new Token(type, token, {
      allowOffline: data.allowOffline ?? false,
      interactiveMode: data.interactiveMode ?? false,
      language: data.language,
      showcaseTTS: data.showcaseTTS ?? false,
      definitions: data.definitions ?? false,
      noui: data.noui ?? false,
      links: data.links?.map((l) => Link.deserialize(l)),
    });
  } catch (e) {
    // bypass sentry filtering with `url_value` naming
    // it's ok to share a token to sentry especially if it's malformed
    captureException(e, { extra: { url_value: token } });
    return new Token(type, token, null);
  }
}
